import React, { useState, useEffect } from 'react';
import { Award, Target, Eye, Heart, Globe, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import PageHero from '../components/PageHero';
import SEO from '../components/SEO';

const About = () => {
  const [settings, setSettings] = useState(null);
  const [certifications, setCertifications] = useState([]);
  const [partnerships, setPartnerships] = useState([]);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/settings/`).then(res => res.json()),
      fetch(`${BACKEND_URL}/api/certifications/`).then(res => res.json()),
      fetch(`${BACKEND_URL}/api/partnerships/`).then(res => res.json())
    ])
      .then(([settingsData, certificationsData, partnershipsData]) => {
        setSettings(settingsData);
        setCertifications(certificationsData);
        setPartnerships(partnershipsData);
      })
      .catch(err => console.error('Error fetching data:', err));
  }, [BACKEND_URL]);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEO 
        title="عن الجامعة"
        description={settings?.about_ar?.substring(0, 160) || "تعرف على جامعة ابن النفيس للعلوم الطبية - رؤيتنا، رسالتنا، أهدافنا وقيمنا."}
        keywords="عن جامعة ابن النفيس, رؤية الجامعة, رسالة الجامعة, أهداف الجامعة"
        url="https://uni-content-fixer.preview.emergentagent.com/about"
      />
      
      <PageHero 
        title="عن الجامعة"
        subtitle="تعرف على جامعة ابن النفيس للعلوم الطبية"
        backgroundImage={settings?.page_about_bg}
      />

      {/* About Text */}
      {settings?.about_ar && (
        <section className="py-16 bg-white dark:bg-gray-900">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg whitespace-pre-line text-justify">
              {settings.about_ar}
            </div>
          </div>
        </section>
      )}

      {/* Vision, Mission, Goals, Values - 4 Cards in 2x2 Grid */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Vision - الرؤية */}
            {settings?.vision_ar && (
              <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-sm hover:shadow-xl transition-shadow">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-[#0F388E] rounded-full flex items-center justify-center mb-4">
                    <Eye className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0F388E] dark:text-white mb-3">
                    الرؤية
                  </h3>
                  <div className="w-16 h-1 bg-[#C9A040] mb-6"></div>
                  <div className="text-gray-600 dark:text-gray-300 leading-relaxed text-base whitespace-pre-line">
                    {settings.vision_ar}
                  </div>
                </div>
              </div>
            )}

            {/* Mission - الرسالة */}
            {settings?.mission_ar && (
              <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-sm hover:shadow-xl transition-shadow">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-[#0F388E] rounded-full flex items-center justify-center mb-4">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0F388E] dark:text-white mb-3">
                    الرسالة
                  </h3>
                  <div className="w-16 h-1 bg-[#C9A040] mb-6"></div>
                  <div className="text-gray-600 dark:text-gray-300 leading-relaxed text-base whitespace-pre-line">
                    {settings.mission_ar}
                  </div>
                </div>
              </div>
            )}

            {/* Goals - الأهداف */}
            {settings?.goals_ar && (
              <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-sm hover:shadow-xl transition-shadow">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-[#0F388E] rounded-full flex items-center justify-center mb-4">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0F388E] dark:text-white mb-3">
                    الأهداف
                  </h3>
                  <div className="w-16 h-1 bg-[#C9A040] mb-6"></div>
                  <div className="text-gray-600 dark:text-gray-300 leading-relaxed text-base whitespace-pre-line">
                    {settings.goals_ar}
                  </div>
                </div>
              </div>
            )}

            {/* Values - القيم */}
            {settings?.values_ar && (
              <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-sm hover:shadow-xl transition-shadow">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-[#0F388E] rounded-full flex items-center justify-center mb-4">
                    <Heart className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0F388E] dark:text-white mb-3">
                    القيم
                  </h3>
                  <div className="w-16 h-1 bg-[#C9A040] mb-6"></div>
                  <div className="text-gray-600 dark:text-gray-300 leading-relaxed text-base whitespace-pre-line">
                    {settings.values_ar}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Leadership Messages - كلمة القيادة الجامعية */}
      {(settings?.founder_name_ar || settings?.president_name_ar) && (
        <section className="py-16 bg-white dark:bg-gray-900">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F388E] dark:text-white mb-3">
                كلمة القيادة الجامعية
              </h2>
              <div className="w-24 h-1 bg-[#C9A040] mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                رؤية قيادية ملهمة نحو التميز والابتكار في التعليم الطبي
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Founder Message - تصميم عمودي مع خلفية ذهبية */}
              {settings?.founder_name_ar && (
                <div className="bg-[#C9A040] rounded-2xl p-8 shadow-lg">
                  <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden">
                    {/* الصورة المربعة الكبيرة */}
                    {settings.founder_image && (
                      <div className="w-full">
                        <img 
                          src={settings.founder_image} 
                          alt={settings.founder_name_ar}
                          className="w-full aspect-square object-cover"
                        />
                      </div>
                    )}
                    {/* المعلومات والنص */}
                    <div className="p-8">
                      <h3 className="text-2xl font-bold text-[#0F388E] dark:text-white mb-2 text-center">
                        {settings.founder_name_ar}
                      </h3>
                      {settings.founder_name_en && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
                          {settings.founder_name_en}
                        </p>
                      )}
                      <div className="flex justify-center mb-6">
                        <span className="bg-[#0F388E] text-white px-6 py-2 rounded-full text-sm font-semibold">
                          مؤسس الجامعة
                        </span>
                      </div>
                      <div className="prose max-w-none">
                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line text-justify">
                          {settings.founder_message_ar}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* President Message - تصميم عمودي مع خلفية زرقاء */}
              {settings?.president_name_ar && (
                <div className="bg-[#0F388E] rounded-2xl p-8 shadow-lg">
                  <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden">
                    {/* الصورة المربعة الكبيرة */}
                    {settings.president_image && (
                      <div className="w-full">
                        <img 
                          src={settings.president_image} 
                          alt={settings.president_name_ar}
                          className="w-full aspect-square object-cover"
                        />
                      </div>
                    )}
                    {/* المعلومات والنص */}
                    <div className="p-8">
                      <h3 className="text-2xl font-bold text-[#0F388E] dark:text-white mb-2 text-center">
                        {settings.president_name_ar}
                      </h3>
                      {settings.president_name_en && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
                          {settings.president_name_en}
                        </p>
                      )}
                      <div className="flex justify-center mb-6">
                        <span className="bg-[#C9A040] text-white px-6 py-2 rounded-full text-sm font-semibold">
                          رئيس الجامعة
                        </span>
                      </div>
                      <div className="prose max-w-none">
                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line text-justify">
                          {settings.president_message_ar}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* الشهادات والعضويات والشراكات الدولية */}
      {(certifications.length > 0 || partnerships.length > 0) && (
        <section className="py-16 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* الشهادات الدولية للجامعة */}
            {certifications.filter(c => c.type === 'certification').length > 0 && (
              <div className="mb-16">
                <div className="text-center mb-8">
                  <h2 className="text-3xl md:text-4xl font-bold text-[#0F388E] dark:text-white mb-3">
                    الشهادات الدولية للجامعة
                  </h2>
                  <div className="w-24 h-1 bg-[#C9A040] mx-auto"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {certifications.filter(c => c.type === 'certification').map((cert) => (
                    <Card key={cert.id} className="hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700 text-center group">
                      <CardContent className="p-6">
                        <div className="w-20 h-20 bg-[#0F388E] dark:bg-[#C9A040] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                          {cert.image || cert.logo ? (
                            <img 
                              src={cert.image || cert.logo} 
                              alt={cert.title_ar}
                              className="w-12 h-12 object-contain"
                            />
                          ) : (
                            <CheckCircle className="w-10 h-10 text-white" />
                          )}
                        </div>
                        <h4 className="text-base font-bold text-[#0F388E] dark:text-white mb-2">
                          {cert.title_ar}
                        </h4>
                        {cert.title_en && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                            {cert.title_en}
                          </p>
                        )}
                        {cert.organization_ar && (
                          <p className="text-sm text-[#C9A040] font-semibold mb-2">
                            {cert.organization_ar}
                          </p>
                        )}
                        {cert.description_ar && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                            {cert.description_ar}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* العضويات الدولية */}
            {certifications.filter(c => c.type === 'membership').length > 0 && (
              <div className="mb-16">
                <div className="text-center mb-8">
                  <h2 className="text-3xl md:text-4xl font-bold text-[#0F388E] dark:text-white mb-3">
                    العضويات الدولية
                  </h2>
                  <div className="w-24 h-1 bg-[#C9A040] mx-auto"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {certifications.filter(c => c.type === 'membership').map((cert) => (
                    <Card key={cert.id} className="hover:shadow-xl transition-all duration-300 border-2 border-[#C9A040] group">
                      <CardContent className="p-6 text-center">
                        {(cert.image || cert.logo) && (
                          <div className="mb-4 h-24 flex items-center justify-center">
                            <img 
                              src={cert.image || cert.logo} 
                              alt={cert.title_ar}
                              className="max-h-full w-auto object-contain group-hover:scale-105 transition-transform"
                            />
                          </div>
                        )}
                        <h4 className="text-lg font-bold text-[#0F388E] dark:text-white mb-2">
                          {cert.title_ar}
                        </h4>
                        {cert.title_en && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                            {cert.title_en}
                          </p>
                        )}
                        {cert.organization_ar && (
                          <p className="text-sm text-[#C9A040] font-semibold mb-2">
                            {cert.organization_ar}
                          </p>
                        )}
                        {cert.description_ar && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                            {cert.description_ar}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* الاتفاقيات والشراكات الدولية */}
            {partnerships.length > 0 && (
              <div>
                <div className="text-center mb-8">
                  <h2 className="text-3xl md:text-4xl font-bold text-[#0F388E] dark:text-white mb-3">
                    الاتفاقيات والشراكات الدولية
                  </h2>
                  <div className="w-24 h-1 bg-[#C9A040] mx-auto"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {partnerships.map((partnership) => (
                    <Card key={partnership.id} className="hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700 group">
                      <CardContent className="p-6">
                        {partnership.partner_logo && (
                          <div className="mb-4 h-20 flex items-center justify-center">
                            <img 
                              src={partnership.partner_logo} 
                              alt={partnership.partner_name_ar}
                              className="max-h-full w-auto object-contain group-hover:scale-105 transition-transform"
                            />
                          </div>
                        )}
                        <h4 className="text-lg font-bold text-[#0F388E] dark:text-white mb-2 text-center">
                          {partnership.partner_name_ar}
                        </h4>
                        {partnership.partner_name_en && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-2">
                            {partnership.partner_name_en}
                          </p>
                        )}
                        {partnership.country && (
                          <div className="flex items-center justify-center gap-2 text-sm text-[#C9A040] font-semibold mb-3">
                            <Globe className="w-4 h-4" />
                            <span>{partnership.country}</span>
                          </div>
                        )}
                        {partnership.description_ar && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 text-center leading-relaxed">
                            {partnership.description_ar}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default About;
